import { supabase } from '../lib/supabase';

/**
 * Centralized subcontract financial calculation service.
 * Computes all financial metrics for a given subcontract (contractId).
 */
export async function getSubcontractSummary(contractId) {
  if (!contractId) {
    return {
      contractValue: 0,
      approvedExtras: 0,
      credits: 0,
      revisedContract: 0,
      totalInvoiced: 0,
      remaining: 0,
      billingProgress: 0,
      extrasBreakdown: [],
      creditsBreakdown: [],
      signedChangeOrders: [],
    };
  }

  // 1) Contract Value
  const { data: subcontractData } = await supabase?.from('subcontracts')?.select('contract_value, pricing_model')?.eq('id', contractId)?.single();

  const pricingModel = subcontractData?.pricing_model || 'fixed';

  // Labor & Material: skip all financial calculations
  if (pricingModel === 'labor_material') {
    return {
      contractValue: null,
      approvedExtras: null,
      credits: null,
      revisedContract: null,
      totalInvoiced: 0,
      remaining: null,
      billingProgress: null,
      extrasBreakdown: [],
      creditsBreakdown: [],
      signedChangeOrders: [],
      invoicesBreakdown: [],
      isLaborMaterial: true,
    };
  }

  const contractValue = parseFloat(subcontractData?.contract_value) || 0;

  // 2) Extra Lines strictly linked to THIS subcontract only — no NULL subcontract_id lines
  const { data: extraLines } = await supabase
    ?.from('project_extra_lines')
    ?.select(`
      id,
      amount,
      description,
      extra:project_extras(id, title, type, status)
    `)
    ?.eq('subcontract_id', contractId);

  const approvedLines = (extraLines || [])?.filter(
    (l) => l?.extra?.status === 'approved'
  );

  const approvedExtras = approvedLines?.filter((l) => l?.extra?.type === 'extra')?.reduce((sum, l) => sum + Number(l?.amount || 0), 0);

  const credits = approvedLines?.filter((l) => l?.extra?.type === 'credit')?.reduce((sum, l) => sum + Number(l?.amount || 0), 0);

  // 3) Build extrasBreakdown and creditsBreakdown grouped by extra.id
  //    Only subcontract-linked lines — no internal LHIAM lines
  const groupByExtra = (lines) => {
    const map = {};

    for (const line of lines) {
      const extraId = line?.extra?.id;
      if (!extraId) continue;
      if (!map?.[extraId]) {
        map[extraId] = {
          extraId,
          title: line?.extra?.title || 'Untitled',
          lines: [],
        };
      }
      map?.[extraId]?.lines?.push({
        description: line?.description || null,
        amount: Number(line?.amount || 0),
      });
    }

    const result = [];
    for (const group of Object.values(map)) {
      const totalAmount = group?.lines?.reduce((s, l) => s + l?.amount, 0);
      result?.push({
        extraId: group?.extraId,
        title: group?.title,
        totalAmount,
        subLines: group?.lines,
      });
    }
    return result;
  };

  const approvedExtraLines = approvedLines?.filter((l) => l?.extra?.type === 'extra');
  const approvedCreditLines = approvedLines?.filter((l) => l?.extra?.type === 'credit');

  const extrasBreakdown = groupByExtra(approvedExtraLines);
  const creditsBreakdown = groupByExtra(approvedCreditLines);

  // 4) Avenants signes. UNE seule liste : celle que l'ecran affiche est aussi
  //    celle qui fait la somme du contrat revise. Le montant garde son signe
  //    (un credit est un avenant negatif) : jamais de Math.abs() ici.
  //    AUCUNE jointure : le total ne doit dependre de rien d'autre que cette table.
  const { data: signedCOs, error: signedCOsError } = await supabase
    ?.from('subcontract_change_orders')
    ?.select('id, co_number, description, amount, status, approved_at, created_at, source_extra_id')
    ?.eq('subcontract_id', contractId)
    ?.eq('status', 'signed');

  // Sans cette liste, le contrat revise serait faux sans que rien ne le dise.
  // On arrete tout : mieux vaut aucun chiffre qu'un contrat revise egal a la
  // valeur du contrat « comme si de rien n'etait ».
  if (signedCOsError) {
    console.error(
      '[getSubcontractSummary] avenants signes illisibles pour le contrat',
      contractId,
      '— contrat revise non calcule :',
      signedCOsError?.message
    );
    throw signedCOsError;
  }

  // Titres des extras sources — AFFICHAGE seulement. Si cette lecture echoue,
  // les titres sont absents et le total ne change pas.
  const sourceExtraIds = [...new Set((signedCOs || [])?.map((co) => co?.source_extra_id)?.filter(Boolean))];
  const extraTitleById = {};
  if (sourceExtraIds?.length > 0) {
    const { data: sourceExtras, error: sourceExtrasError } = await supabase
      ?.from('project_extras')
      ?.select('id, title')
      ?.in('id', sourceExtraIds);
    if (sourceExtrasError) {
      console.error('[getSubcontractSummary] titres des extras sources illisibles :', sourceExtrasError?.message);
    }
    for (const e of sourceExtras || []) extraTitleById[e?.id] = e?.title || null;
  }

  // Tri : numero d'avenant (le nombre apres AV dans co_number), puis date de
  // signature, puis date de creation. Sans numero AV, ou sans date : en dernier.
  const avNumberOf = (coNumber) => {
    const match = coNumber?.match(/\.AV(\d+)$/i);
    return match ? parseInt(match?.[1], 10) : null;
  };
  const byAvNumber = (a, b) => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  };
  const byDate = (a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return new Date(a) - new Date(b);
  };

  const signedChangeOrders = (signedCOs || [])
    ?.map((co) => ({
      id: co?.id,
      coNumber: co?.co_number || null,
      avNumber: avNumberOf(co?.co_number),
      description: co?.description || null,
      amount: parseFloat(co?.amount) || 0,
      status: co?.status,
      approvedAt: co?.approved_at || null,
      createdAt: co?.created_at || null,
      sourceExtraId: co?.source_extra_id || null,
      sourceExtraTitle: extraTitleById?.[co?.source_extra_id] || null,
    }))
    ?.sort((a, b) =>
      byAvNumber(a?.avNumber, b?.avNumber) ||
      byDate(a?.approvedAt, b?.approvedAt) ||
      byDate(a?.createdAt, b?.createdAt)
    );

  const approvedCOsTotal = signedChangeOrders?.reduce((sum, co) => sum + co?.amount, 0);

  const revisedContract = contractValue + approvedCOsTotal;

  // 5) Total Invoiced — join sub_invoices → sub_invoice_lines
  // Query by subcontract_id to capture ALL invoice types (contract, extra, credit)
  const { data: invoicesData } = await supabase?.from('sub_invoices')?.select('id, invoice_number, invoice_date, subcontract_id, status')?.eq('subcontract_id', contractId);

  let totalInvoiced = 0;
  let invoicesBreakdown = [];

  if (invoicesData && invoicesData?.length > 0) {
    const invoiceIds = invoicesData?.map((inv) => inv?.id);

    const { data: linesData } = await supabase?.from('sub_invoice_lines')?.select('amount, description, sub_invoice_id')?.in('sub_invoice_id', invoiceIds);

    totalInvoiced = (linesData || [])?.reduce(
      (sum, line) => sum + (parseFloat(line?.amount) || 0),
      0
    );

    // Build invoicesBreakdown grouped by invoice
    const linesMap = {};
    for (const line of (linesData || [])) {
      const invId = line?.sub_invoice_id;
      if (!invId) continue;
      if (!linesMap?.[invId]) linesMap[invId] = [];
      linesMap?.[invId]?.push({
        description: line?.description || null,
        amount: parseFloat(line?.amount) || 0,
      });
    }

    invoicesBreakdown = (invoicesData || [])?.map((inv) => {
      const lines = linesMap?.[inv?.id] || [];
      const total = lines?.reduce((s, l) => s + l?.amount, 0);
      return {
        invoiceId: inv?.id,
        invoiceNumber: inv?.invoice_number || null,
        invoiceDate: inv?.invoice_date || null,
        subcontractId: inv?.subcontract_id || null,
        status: inv?.status || null,
        total,
        lines,
      };
    });

    invoicesBreakdown?.sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate));
  }

  // 6) Remaining
  const remaining = revisedContract - totalInvoiced;

  // 7) Billing Progress
  const billingProgress = revisedContract > 0 ? totalInvoiced / revisedContract : 0;

  return {
    contractValue,
    approvedExtras,
    credits,
    revisedContract,
    totalInvoiced,
    remaining,
    billingProgress,
    extrasBreakdown,
    creditsBreakdown,
    invoicesBreakdown,
    signedChangeOrders,
  };
}
